import { ROUTES } from '@/routes/routes';
import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { store } from '@/store';
import { logout, refreshAccessToken } from '@/store/slices/authSlice';
import { INTERVIEW_AGENT_BASE_URL, RECRUITMENT_AGENT_BASE_URL } from '@/config';

// Create axios instance for the main Interview Agent API
const createApiClient = (getBaseUrl: string): AxiosInstance => {
  const apiClient: AxiosInstance = axios.create({
    baseURL: getBaseUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor for authentication
  apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const state = store.getState();
      const token = state.auth.accessToken;

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling and token refresh
  apiClient.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Handle 401 errors (unauthorized)
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Attempt to refresh token
          const result = await store.dispatch(refreshAccessToken());

          if (refreshAccessToken.fulfilled.match(result)) {
            // Retry original request with new token
            const newToken = store.getState().auth.accessToken;
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return apiClient(originalRequest);
          }
        } catch {
          // Refresh failed, logout user
          store.dispatch(logout());
          window.location.href = ROUTES.login;
        }
      }

      // Handle other errors
      const message = extractErrorMessage(error);
      return Promise.reject(new Error(message));
    }
  );

  // Error message extraction helper
  function extractErrorMessage(error: AxiosError): string {
    if (error.response?.data) {
      const data = error.response.data as Record<string, unknown>;
      if (typeof data.message === 'string') {
        return data.message;
      }
      if (typeof data.error === 'string') {
        return data.error;
      }
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        return data.errors.join(', ');
      }
    }
    return error.message || 'An unexpected error occurred';
  }
  return apiClient;
};

// Service-specific API clients

const interviewAgentApi = createApiClient(INTERVIEW_AGENT_BASE_URL);
const recruitmentAgentApi = createApiClient(RECRUITMENT_AGENT_BASE_URL);
const faceDetectionClient = createApiClient('https://aivision.share.zrok.io/'); // Face detection API base URL

// // Helper functions for common HTTP methods
export const api = {
  interviewAgent: {
    get: <T>(url: string, config = {}) =>
      interviewAgentApi.get<T>(url, config).then(res => res.data),

    post: <T>(url: string, data?: unknown, config = {}) =>
      interviewAgentApi.post<T>(url, data, config).then(res => res.data),

    put: <T>(url: string, data?: unknown, config = {}) =>
      interviewAgentApi.put<T>(url, data, config).then(res => res.data),

    patch: <T>(url: string, data?: unknown, config = {}) =>
      interviewAgentApi.patch<T>(url, data, config).then(res => res.data),

    delete: <T>(url: string, config = {}) =>
      interviewAgentApi.delete<T>(url, config).then(res => res.data),

    upload: <T>(
      url: string,
      formData: FormData,
      onProgress?: (progress: number) => void
    ) =>
      interviewAgentApi
        .post<T>(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: progressEvent => {
            if (onProgress && progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(progress);
            }
          },
        })
        .then(res => res.data),
  },

  recruitmentAgent: {
    post: <T>(url: string, data?: unknown, config = {}) =>
      recruitmentAgentApi.post<T>(url, data, config).then(res => res.data),
  },
  faceDetectionApi: {
    post: <T>(url: string, data?: unknown, config = {}) =>
      faceDetectionClient.post<T>(url, data, config).then(res => res.data),
  },
};
